import React, { useState, useEffect } from 'react';
import { find, pipe, prop, map, uniqBy } from 'ramda';
import Table from '../Table';
import {
    getServiceConfigurationValues,
} from '../../sources/service';
import { addNotification } from '../../actions/reduxActions/notification';
import { connect } from 'react-redux';

/**
 * @param {Object[]} configurationValues - List of supported configuration values for a service
 * @param {string} configurationValues[].name - The developerName of the configuration value
 * @param {string} configurationValues[].valueType - The contentType of the configuration value
 * @param {string} configurationValues[].valueId - The id of the assigned value
 * @param {string} configurationValues[].valueName - The id of the assigned value
 * @param {string} configurationValues[].isInCurrentService - If this value exists in the current Service definition
 */
const ConfigurationList = ({ configurationValues, isLoading, url, username, password, authenticationToken, tenantId, addNotification }) => {

    const [allConfigurationOptions, setAllConfigurationOptions] = useState([]);
    const [isLoadingConfigOptions, setIsLoadingConfigOptions] = useState(true);

    // Only happens on first render
    useEffect(() => {
        async function fetchAllConfigurationOptions() {
            if ( // Only if we've been given a valid set of login credentials, or none, should we try to get the service configuration values
                (username && password) || (!username && !password)
            ) {
                // Need to fire individual api calls for each value to get their name for the list
                getServiceConfigurationValues({
                    token: authenticationToken,
                    tenantId,
                    uri: url,
                    basicUsername: username,
                    basicPassword: password,
                }).then((serviceConfig) => {
                    // Grab the options the Service told us
                    let allOptions = pipe(
                        prop('outputs'),
                        find(o => o.developerName === 'Configuration Values'),
                        prop('objectData'),
                        map(
                            configData => configData.properties.reduce(
                                (acc, current) => {
                                    if (current.developerName === 'DeveloperName') {
                                        acc.name = current.contentValue;
                                        const assignedConfigurationValue = configurationValues.find(value => value.name === current.contentValue);
                                        if (assignedConfigurationValue) { // If this value was assigned by the builder, get the Value information they assigned it to
                                            acc.valueName = assignedConfigurationValue.valueName;
                                            acc.valueId = assignedConfigurationValue.valueId;
                                            acc.valueType = assignedConfigurationValue.valueType;
                                        }
                                        acc.isInCurrentService = true; // This value comes from the Service definition, so we know if exists
                                    }
                                    if (current.developerName === 'ContentType') {
                                        acc.valueType = current.contentValue.replace('Content', ''); // Remove "Content" from the type
                                    }
                                    return acc;
                                },
                                {},
                            ),
                        ),
                    )(serviceConfig);

                    // Go over the configurationValues from the engine and see if any now don't work
                    const assignedConfigValues = configurationValues.map((assignedConfigValue) => {
                        assignedConfigValue.isInCurrentService = allOptions.find(value => value.name === assignedConfigValue.name).length > 0;
                        return assignedConfigValue;
                    });

                    // Add the options the Engine told us, but remove dupes
                    allOptions = uniqBy(
                        prop('name'),
                        allOptions.concat(assignedConfigValues),
                    );

                    setAllConfigurationOptions(allOptions);
                    setIsLoadingConfigOptions(false);
                }).catch((error) => {
                    // The call to the external service failed,
                    // so we just use the configuration Values from the Service data we have,
                    // these will only be the config Values for the Service that they have set
                    setAllConfigurationOptions(configurationValues);
                    setIsLoadingConfigOptions(false);
                    const errorObject = JSON.parse(error.message);
                    addNotification({
                        type: 'error',
                        message: `${errorObject.statusCode} - ${errorObject.message}: ${errorObject.uri}`,
                        isPersistent: true,
                    });
                });
            }
        }
        if (isLoading === false) {
            fetchAllConfigurationOptions();
        }
    }, [isLoading]);

    const columns = [
        {
            renderHeader: () => 'Name',
            renderCell: ({ item: configurationValue }) => (
                <span className="nowrap">
                    {
                        configurationValue.isInCurrentService ?
                            configurationValue.name :
                            `⚠ ${configurationValue.name}`
                    }
                </span>
            ),
            size: '1px',
        },
        {
            renderHeader: () => 'Value',
            renderCell: ({ item: configurationValue }) => (
                <div className="flex align-center">
                    <div className="flex-column">
                        {
                            configurationValue.valueId ?
                                <strong>{configurationValue.valueName}</strong> :
                                <button className="link-emulate">Select</button>
                        }
                        <span>{configurationValue.valueType}</span>
                    </div>
                    {
                        configurationValue.isInCurrentService === false || configurationValue.valueId ?
                            (
                                <div className="flex-child-right">
                                    {configurationValue.isInCurrentService === false ?
                                        'Warning: This Configuration Value doesn\'t exist in the Service Configuration Values any more' : null}
                                    {configurationValue.valueId ?
                                        (
                                            <>
                                                <button className="link-emulate margin-left">Select</button>
                                                <button className="link-emulate margin-left">Edit</button>
                                                <button className="link-emulate margin-left">Remove</button>
                                            </>
                                        ) : null}
                                </div>
                            ) : null
                    }
                </div>
            ),
        },
    ];

    return (
        <Table
            rowClassName={item => item.isInCurrentService === false ? 'alert-warning' : null}
            isLoading={isLoading || isLoadingConfigOptions}
            className="table margin-top"
            items={allConfigurationOptions}
            columns={columns}
        />
    );
};

const mapDispatchToProps = {
    addNotification,
};

export default connect(
    null,
    mapDispatchToProps,
)(ConfigurationList);
